import React from 'react';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import { SectionSidebarMenuGroup } from './index';

/**
 * Renders menu items as Tab components
 */
const MenuItems: React.FC<{ items: Array<{ id: string; label: string }> }> = ({
  items,
}) => (
  <>
    {items.map((item) => (
      <Tab key={item.id} value={item.id} label={item.label} />
    ))}
  </>
);

/**
 * Renders a menu group with label and items
 */
export interface GroupedMenuProps {
  group: SectionSidebarMenuGroup;
  activeId: string;
  onSelect: (id: string) => void;
  variant: ComponentVariant;
  appearance: ComponentAppearance;
}

/**
 * GroupedMenu component - displays a menu group with a label and tabs
 * @param group - The menu group containing id, label, and items
 * @param activeId - The currently active menu item id
 * @param onSelect - Callback when a menu item is selected
 * @param variant - Visual variant of the tabs
 * @param appearance - Appearance style of the tabs
 */
export const GroupedMenu: React.FC<GroupedMenuProps> = ({
  group,
  activeId,
  onSelect,
  variant,
  appearance,
}) => {
  const isActiveInGroup = group.items.some((item) => item.id === activeId);
  const groupActiveId = isActiveInGroup ? activeId : '';

  return (
    <div className="flex flex-col gap-2">
      <Text
        size="tiny"
        color="secondary"
        className="uppercase tracking-[0.08em] px-1"
      >
        {group.label}
      </Text>

      <Tabs
        value={groupActiveId}
        onChange={onSelect}
        orientation="vertical"
        variant={variant}
        appearance={appearance}
        size="md"
        className="w-full"
        align="left"
      >
        <MenuItems items={group.items} />
      </Tabs>
    </div>
  );
};
